import { useNamespaceSelector } from '@omaze/redux';
import React, { SetStateAction, Dispatch, ReactElement, useEffect, useState, useRef, RefObject, FC } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { Dispatch as reduxDispatch } from 'redux';

import { OzIcon, ICON_TYPE, ICON_COLOR, ICON_SIZE } from '../Icon/Icon.component';

import styles from './Tooltip.module.css';
import { tooltipDisplayName } from './Tooltip.qa';
import { createTooltipRemoveAction } from './TooltipModule/actions/RemoveTooltipMessage/RemoveTooltipMessage';
import { selectTooltipMessage } from './TooltipModule/selectors/SelectTooltipMessage/SelectTooltipMessage';
import { ITooltipMessage } from './TooltipModule/Tooltip.state';

export interface ITooltipProps {
    onOpen?: () => void;
    onClose?: () => void;
    customClass?: string;
    'data-testid'?: string;
}

const pxUnit = 'px';

export function OzTooltip (props: ITooltipProps): ReactElement | null {
    const { onOpen, onClose, 'data-testid': dataTestId, customClass = '' } = props;
    const tooltipRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
    const rDispatch: reduxDispatch = useDispatch();

    const [customStyle, setCustomStyle]: [Record<string, any>, Dispatch<SetStateAction<Record<string, any>>>] = (
        useState<Record<string, any>>({})
    );

    const tooltipMessage: ITooltipMessage = useNamespaceSelector(selectTooltipMessage);

    function shouldDisplay (tooltipMessage: ITooltipMessage): boolean {
        if (!tooltipMessage || !tooltipMessage.messageComponent ||
            typeof tooltipMessage.messageComponent !== 'function' ||
            !tooltipMessage.ref ||
            tooltipMessage.ref.topBound === null
        ) {
            return false;
        }

        return true;
    }

    useEffect((): void => {
        if (!shouldDisplay(tooltipMessage)) {
            return;
        }

        const { ref } = tooltipMessage;

        if (!ref || !ref.topBound || !ref.leftBound || !ref.offsetHeight) {
            return;
        }

        const xPadding: number = 8;
        const yPadding: number = 16;
        const contentElementTopBound: number = ref.topBound;
        const contentElementHeight: number = ref.offsetHeight;
        const tooltipElementHeight: number = tooltipRef.current?.offsetHeight || 0;
        const contentElementLeftBound: number = ref.leftBound;
        const windowWidth: number = window.screen.width;
        const tooltipElementWidth: number = tooltipRef.current?.offsetWidth || 0;
        const windowScrollY: number = window.scrollY;
        let top: number = 0;
        let left: number = 0;

        if (contentElementTopBound > (tooltipElementHeight + xPadding)) {
            top = contentElementTopBound + windowScrollY - (tooltipElementHeight + xPadding);
        } else {
            top = contentElementTopBound + contentElementHeight + xPadding + windowScrollY;
        }

        if (contentElementLeftBound < (windowWidth - tooltipElementWidth)) {
            left = contentElementLeftBound;
        } else {
            left = windowWidth - tooltipElementWidth - yPadding;
        }

        setCustomStyle({
            top: `${top}${pxUnit}`,
            left: `${left}${pxUnit}`,
            visibility: 'visible',
        });

        if (onOpen) {
            onOpen();
        }
    }, [tooltipMessage, onOpen]);

    function renderTooltip (Child: FC | null, closeTooltip: () => void): ReactElement {
        return ReactDOM.createPortal((
            <div
                ref={tooltipRef}
                style={customStyle}
                data-testid={dataTestId}
                className={`
                    absolute flex rounded-sm font-medium invisible top-0 left-0
                    z-50 type-caption bg-secondary-variant shadow-tooltip
                    ${customClass} ${styles.tooltip}`}
            >
                <div className='p-2 w-9/10'>
                    {Child && <Child />}
                </div>
                <div className='relative w-1/10 mt-2 mr-2'>
                    <span onClick={closeTooltip}>
                        <OzIcon
                            className='h-2 w-2 absolute top-0 right-0 hover:cursor-pointer'
                            alt="Close"
                            color={ICON_COLOR.DARK}
                            size={ICON_SIZE.MEDIUM}
                            type={ICON_TYPE.OZ_CLOSE}
                        />
                    </span>
                </div>
            </div>
        ), document.body);
    }

    function closeTooltip (): void {
        rDispatch(createTooltipRemoveAction());

        if (onClose) {
            onClose();
        }
    }

    if (!shouldDisplay(tooltipMessage)) {
        return null;
    }

    return (
        renderTooltip(tooltipMessage.messageComponent, closeTooltip)
    );
}

OzTooltip.displayName = tooltipDisplayName;
